// Page ranges for Split and PDF to JPG (FR-11, FR-15). Pure: no pdf-lib, so
// pages can use these without downloading the PDF library.

export type Groups = number[][];

/** "1-3, 5, 8-" → [[0,1,2],[4],[7,8,9]] for a 10-page PDF, or a friendly error. */
export function parseRanges(
  text: string,
  pageCount: number,
): { groups: Groups } | { error: string } {
  const parts = text
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return { error: "Type the pages you want, like 1-3, 5." };
  }
  const groups: Groups = [];
  for (const part of parts) {
    const m = part.match(/^(\d+)\s*(?:[-–—]|to)\s*(\d*)$|^(\d+)$/i);
    if (!m) {
      return {
        error: `“${part}” isn't a page or a range. Use numbers like 1-3 or 5.`,
      };
    }
    const from = Number(m[1] ?? m[3]);
    const to = m[3] ? from : m[2] ? Number(m[2]) : pageCount; // "8-" = to the end
    if (from < 1 || to < 1) return { error: "Pages start at 1." };
    if (from > pageCount || to > pageCount) {
      return {
        error: `This PDF has ${pageCount} ${pageCount === 1 ? "page" : "pages"}, so “${part}” is too high.`,
      };
    }
    if (to < from) {
      return {
        error: `In “${part}” the first page should come before the last.`,
      };
    }
    groups.push(Array.from({ length: to - from + 1 }, (_, i) => from - 1 + i));
  }
  return { groups };
}

export function everyNPages(pageCount: number, n: number): Groups {
  const groups: Groups = [];
  for (let start = 0; start < pageCount; start += n) {
    groups.push(
      Array.from(
        { length: Math.min(n, pageCount - start) },
        (_, i) => start + i,
      ),
    );
  }
  return groups;
}

export function eachPage(pageCount: number): Groups {
  return everyNPages(pageCount, 1);
}

/** Pages 1-3, 5 → "pages-1-3_5"; page 5 → "page-5" (for file names). */
export function groupLabel(group: number[]): string {
  if (group.length === 1) return `page-${group[0] + 1}`;
  const runs: string[] = [];
  let start = group[0];
  for (let i = 1; i <= group.length; i++) {
    if (i === group.length || group[i] !== group[i - 1] + 1) {
      const end = group[i - 1];
      runs.push(start === end ? `${start + 1}` : `${start + 1}-${end + 1}`);
      start = group[i];
    }
  }
  return `pages-${runs.join("_")}`;
}
